/**
 * @file discarded-orders-table.tsx
 * @module app/modules/orders-delivery/components/delivery-plan/discarded-orders-table
 * @description Read-only table listing orders discarded from an edit-route plan
 * preview (omitted due to vehicle capacity or delivery duration constraints).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 2.1.2
 */

import { Card } from 'react-bootstrap';
import Method from '../../../../../utils/methods';
import { Manager, ServiceMember } from '../../../../../utils/constants';
import { getOrderTypeLabel, kgToTons } from '../../utils/delivery-plan-details-helpers';
import { normalizeOrderMetric } from '../../utils/order-metric-values';

interface DiscardedOrdersTableProps {
  discardedOrder: any[];
  currentUser: any;
  selectLoading: boolean;
}

const DiscardedOrdersTable = ({
  discardedOrder,
  currentUser,
  selectLoading,
}: DiscardedOrdersTableProps) => {
  return (
    <Card className="border border-r10px">
      <Card.Body className="p-0">
        <div className="table-responsive">
          <div className="mh-400px scroll-y me-n7 pe-7">
            <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
              <thead>
                <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                  <th className="min-w-100px" tabIndex={0} rowSpan={1} colSpan={1}>
                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                      <label className="form-check-label fs-16 fw-600 text-dark">
                        Order Id
                      </label>
                    </div>
                  </th>

                  <th className="min-w-md-200px min-w-125px">Customer name</th>
                  <th className="min-w-125px">Order Type</th>
                  {currentUser?.role.length ? (
                    currentUser?.role.some(
                      (obj: any) =>
                        obj.type !== Manager || obj.type !== ServiceMember,
                    ) ? (
                      <></>
                    ) : (
                      <th className="min-w-140px">Ordered by</th>
                    )
                  ) : (
                    <></>
                  )}
                  <th className="min-w-md-125px">Quantity</th>
                  <th className="min-w-md-125px">Weight</th>
                  <th className="min-w-md-125px min-w-100px">Total amount</th>
                </tr>
              </thead>
              <tbody className="text-danger">
                {selectLoading ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3 fs-20 text-dark fw-600">
                        Please wait while processing...
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {discardedOrder.map((orderVal: any, goodIndex: number) => (
                      <tr key={goodIndex}>
                        <td>
                          <div className="form-check form-check-sm form-check-custom form-check-solid">
                            <div className="d-flex align-items-start flex-column ms-2">
                              <div className="d-flex flex-row">
                                <span className={' d-block fs-15 fw-500'}>
                                  {Method.convertDateToDDMMYYYYHOURS(
                                    orderVal._createdAt,
                                  )}
                                </span>
                              </div>
                              <span className={' d-block'}>{orderVal.refKey}</span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span className="fs-15 fw-500 d-block">
                            {orderVal.customerDetails.name}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500 d-block">
                            {getOrderTypeLabel(orderVal)}
                          </span>
                        </td>
                        {currentUser?.role.length ? (
                          currentUser?.role.some(
                            (obj: any) =>
                              obj.type !== Manager || obj.type !== ServiceMember,
                          ) ? (
                            <></>
                          ) : (
                            <td>{/* Placeholder for ordered by section */}</td>
                          )
                        ) : (
                          <></>
                        )}
                        <td>
                          <span className="fs-15 fw-500">
                            {`${normalizeOrderMetric(orderVal.mDimensions?.totalVariantsStockCount) ?? '-'} items`}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {kgToTons(orderVal.mDimensions?.orderWeight) +
                              ' tons'}
                          </span>
                        </td>
                        <td>
                          <span className="fs-15 fw-500">
                            {'TSh ' +
                              Method.getGeneralizedAmount(
                                orderVal.postData.payment.totalCharge,
                              )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default DiscardedOrdersTable;
